import {
  type CreateInkColorInput,
  colorCodeSchema,
  createInkColorInputSchema,
  MAX_INK_COLOR_NAME_LENGTH,
} from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { z } from 'zod';
import { firstErrorPerField, koreanMessageOr } from '@/shared/lib';
import { Button, Input, Label } from '@/shared/ui';

const FIELD_KEYS = ['name', 'colorCode'] as const;
const NAME_HINT = `잉크 색상명은 1~${MAX_INK_COLOR_NAME_LENGTH}자로 입력해 주세요.`;
const COLOR_CODE_HINT = '색상 코드는 #RRGGBB 형식이어야 합니다.';
const INITIAL_COLOR_CODE = '#000000';
/** '#RRGGBB' 의 글자 수. */
const COLOR_CODE_LENGTH = 7;

type FieldErrors = Partial<Record<keyof CreateInkColorInput, string>>;

interface InkColorFormModalProps {
  readonly isPending: boolean;
  /** 서버 거부 — 모달 위 알림. */
  readonly errorMessage: string | null;
  /** 살아 있는 잉크와 이름이 겹친다 — 이름 칸 아래. */
  readonly nameErrorMessage: string | null;
  readonly onSubmit: (input: CreateInkColorInput) => void;
  readonly onClose: () => void;
}

/**
 * 잉크 색상 추가 모달 — 이름과 색상(피커·HEX 칸이 같은 값을 쓴다)을 받는다. 서버와 같은 계약 스키마로 먼저 검사하고,
 * zod 기본 영어 문구는 한국어 문구로 바꿔 보인다. 색상 코드는 등록한 뒤 바꿀 수 없다.
 */
export const InkColorFormModal = ({
  isPending,
  errorMessage,
  nameErrorMessage,
  onSubmit,
  onClose,
}: InkColorFormModalProps): ReactElement => {
  const [name, setName] = useState('');
  const [colorCode, setColorCode] = useState(INITIAL_COLOR_CODE);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const nameError = fieldErrors.name ?? nameErrorMessage;
  // 피커는 #rrggbb 만 받는다 — HEX 칸이 아직 완성되지 않았으면 마지막 기본값을 보인다.
  const pickerValue = colorCodeSchema.safeParse(colorCode).success
    ? colorCode.toLowerCase()
    : INITIAL_COLOR_CODE;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = createInkColorInputSchema.safeParse({ name, colorCode });

    if (parsed.success) {
      setFieldErrors({});
      onSubmit(parsed.data);
      return;
    }

    const errors = firstErrorPerField(z.flattenError(parsed.error).fieldErrors, FIELD_KEYS);
    setFieldErrors({
      ...(errors.name === undefined ? {} : { name: koreanMessageOr(errors.name, NAME_HINT) }),
      ...(errors.colorCode === undefined
        ? {}
        : { colorCode: koreanMessageOr(errors.colorCode, COLOR_CODE_HINT) }),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="잉크 색상 추가"
        className="w-full max-w-sm rounded-lg border bg-background p-5"
      >
        <p className="font-medium">잉크 색상 추가</p>

        {errorMessage !== null ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} noValidate className="mt-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ink-color-form-name">잉크 색상명</Label>
            <Input
              id="ink-color-form-name"
              value={name}
              maxLength={MAX_INK_COLOR_NAME_LENGTH}
              placeholder="예: 먹색"
              onChange={(event) => setName(event.target.value)}
              hasError={nameError !== null}
              aria-describedby={nameError !== null ? 'ink-color-form-name-error' : undefined}
              autoFocus
            />
            {nameError !== null ? (
              <p id="ink-color-form-name-error" className="text-xs text-destructive">
                {nameError}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ink-color-form-code">HEX 코드</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="색상 선택"
                className="size-9 shrink-0 cursor-pointer rounded border"
                value={pickerValue}
                onChange={(event) => setColorCode(event.target.value.toUpperCase())}
              />
              <Input
                id="ink-color-form-code"
                value={colorCode}
                maxLength={COLOR_CODE_LENGTH}
                placeholder="#1A1A1A"
                onChange={(event) => setColorCode(event.target.value)}
                hasError={fieldErrors.colorCode !== undefined}
                aria-describedby={
                  fieldErrors.colorCode !== undefined ? 'ink-color-form-code-error' : undefined
                }
              />
            </div>
            {fieldErrors.colorCode !== undefined ? (
              <p id="ink-color-form-code-error" className="text-xs text-destructive">
                {fieldErrors.colorCode}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                색상 코드는 등록한 뒤 바꿀 수 없습니다.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" size="sm" isLoading={isPending}>
              추가하기
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
